import { animate, createScope, stagger } from "animejs"
import {
  ArrowDownRight,
  ArrowUp,
  ArrowUpRight,
  Download,
  Mail,
  Moon,
  Sun,
} from "lucide-react"
import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react"

import { Button, buttonVariants } from "@/components/ui/button"
import { BouslaIbnMajidPreview, CardzoneRoutingPreview } from "@/components/product-previews"
import { bouslaClients, cardzonePartners, copy, projectLinks } from "@/content"
import { cn } from "@/lib/utils"
import { usePreferences } from "@/preferences"
import { fetchZatcaDownloads, ZATCA_DOWNLOADS_FALLBACK } from "./rubygems"
import amazingTeamLogo from "../public/brand/amazing-team.png"
import bouslaLogo from "../public/brand/bousla.png"
import cardzoneLogo from "../public/brand/cardzone.png"
import propertiesAfter from "../public/brand/properties-after.webp"
import propertiesBefore from "../public/brand/properties-before.webp"
import daleelStoreLogo from "../public/clients/daleel-store.png"

function usePageMotion(root: RefObject<HTMLDivElement | null>, locale: string) {
  useEffect(() => {
    const element = root.current
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    if (!element || reducedMotion) return

    let observer: IntersectionObserver | undefined

    const scope = createScope({ root: element }).add(() => {
      animate("[data-hero-item]", {
        opacity: [0, 1],
        y: [24, 0],
        duration: 850,
        delay: stagger(85),
        ease: "out(4)",
      })

      element
        .querySelectorAll<SVGGElement>(".prism-piece-entry")
        .forEach((piece, index) => {
          const startX = [-72, 0, 72][index]
          const startY = [38, 74, 38][index]
          const startRotate = [-12, 7, 12][index]

          animate(piece, {
            opacity: [0, 1],
            x: [startX, 0],
            y: [startY, 0],
            rotate: [startRotate, 0],
            duration: 1_080,
            delay: 360 + index * 130,
            ease: "out(5)",
          })
        })

      element.querySelectorAll<SVGGElement>(".prism-piece").forEach((piece, index) => {
        const direction = index % 2 === 0 ? 1 : -1

        animate(piece, {
          x: [direction * -2, direction * (7 + index * 1.5)],
          y: [direction * 2, direction * -1 * (5 + index)],
          rotate: [direction * -0.35, direction * (1.25 + index * 0.28)],
          duration: 6_200 + index * 820,
          delay: 1_700 + index * 180,
          alternate: true,
          loop: true,
          ease: "inOut(3)",
        })
      })

      animate(".wonder-prism", {
        y: [-8, 10],
        rotate: [-1.6, 1.6],
        duration: 8_600,
        delay: 1_500,
        alternate: true,
        loop: true,
        ease: "inOut(2)",
      })

      animate(".prism-aura", {
        strokeDashoffset: [0, -144],
        opacity: [0.16, 0.42],
        duration: 7_200,
        loop: true,
        ease: "linear",
      })

      animate(".prism-core", {
        scale: [0.96, 1.035],
        opacity: [0.42, 0.7],
        duration: 4_600,
        delay: 1_200,
        alternate: true,
        loop: true,
        ease: "inOut(3)",
      })

      animate(".hero-signal__line", {
        x: [-5, 7],
        scaleX: [0.35, 1],
        opacity: [0.14, 0.62],
        duration: 2_300,
        delay: stagger(145),
        alternate: true,
        loop: true,
        ease: "inOut(2)",
      })

      const revealTargets = element.querySelectorAll<HTMLElement>("[data-reveal]")
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return

            animate(entry.target, {
              opacity: [0, 1],
              y: [30, 0],
              duration: 720,
              ease: "out(4)",
            })
            observer?.unobserve(entry.target)
          })
        },
        { threshold: 0.12, rootMargin: "0px 0px -7%" },
      )

      revealTargets.forEach((target) => observer?.observe(target))
    })

    return () => {
      observer?.disconnect()
      scope.revert()
    }
  }, [locale, root])
}

function ExternalLink({
  href,
  children,
  className,
  label,
}: {
  href: string
  children: ReactNode
  className?: string
  label?: string
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={className}
      aria-label={label}
    >
      {children}
    </a>
  )
}

function GitHubMark({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 .7a11.5 11.5 0 0 0-3.64 22.42c.58.1.79-.25.79-.56v-2.23c-3.23.7-3.91-1.37-3.91-1.37-.53-1.34-1.29-1.7-1.29-1.7-1.05-.72.08-.7.08-.7 1.17.08 1.78 1.2 1.78 1.2 1.04 1.77 2.72 1.26 3.38.96.1-.75.4-1.26.74-1.55-2.58-.3-5.29-1.29-5.29-5.69 0-1.26.45-2.28 1.19-3.09-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.16 1.18a10.96 10.96 0 0 1 5.76 0c2.2-1.49 3.16-1.18 3.16-1.18.63 1.59.23 2.76.11 3.05.74.81 1.19 1.83 1.19 3.09 0 4.41-2.72 5.39-5.3 5.68.42.36.79 1.06.79 2.14v3.17c0 .31.21.67.8.56A11.5 11.5 0 0 0 12 .7Z" />
    </svg>
  )
}

function Wordmark({ compact = false }: { compact?: boolean }) {
  return (
    <span className="wordmark">
      <span className={cn("wordmark__mark", compact && "wordmark__mark--compact")}>
        <img src={amazingTeamLogo} alt="" />
      </span>
      {!compact && (
        <span className="wordmark__text">
          <span>The Amazing</span>
          <span>Team</span>
        </span>
      )}
    </span>
  )
}

function Header() {
  const { locale, setLocale, theme, setTheme } = usePreferences()
  const c = copy[locale]

  return (
    <header className="site-header">
      <a href="#top" className="header-brand" aria-label="The Amazing Team">
        <Wordmark />
      </a>

      <nav className="header-nav" aria-label={c.accessibility.menu}>
        <a href="#work">{c.nav.work}</a>
        <a href="#open-source">{c.nav.openSource}</a>
        <a href="#contact">{c.nav.contact}</a>
      </nav>

      <div className="header-actions">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          aria-label={theme === "dark" ? c.accessibility.light : c.accessibility.dark}
          title={c.accessibility.theme}
        >
          {theme === "dark" ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="language-toggle"
          onClick={() => setLocale(locale === "en" ? "ar" : "en")}
          aria-label={c.accessibility.language}
        >
          <span aria-hidden="true">{locale === "en" ? "AR" : "EN"}</span>
        </Button>
      </div>
    </header>
  )
}

function HeroPrism({ label }: { label: string }) {
  return (
    <div className="hero-prism" role="img" aria-label={label} data-hero-item>
      <svg
        className="wonder-prism"
        viewBox="0 0 600 600"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="prism-left" x1="95" y1="480" x2="305" y2="70">
            <stop stopColor="#F3CDD2" />
            <stop offset="0.52" stopColor="#FFF9F3" />
            <stop offset="1" stopColor="#FFFFFF" />
          </linearGradient>
          <linearGradient id="prism-bottom" x1="95" y1="480" x2="525" y2="405">
            <stop stopColor="#FFFFFF" />
            <stop offset="0.55" stopColor="#FFF8F1" />
            <stop offset="1" stopColor="#EFC3C9" />
          </linearGradient>
          <linearGradient id="prism-right" x1="280" y1="65" x2="525" y2="405">
            <stop stopColor="#F6D8DB" />
            <stop offset="0.48" stopColor="#FFFFFF" />
            <stop offset="1" stopColor="#FFF5EE" />
          </linearGradient>
          <filter id="prism-shadow" x="-30%" y="-30%" width="160%" height="180%">
            <feDropShadow dx="0" dy="22" stdDeviation="22" floodColor="#6A0010" floodOpacity="0.25" />
          </filter>
          <filter id="core-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="18" />
          </filter>
        </defs>

        <path
          className="prism-aura"
          d="M280 28 564 418 82 505 280 28Z"
          stroke="currentColor"
          strokeDasharray="3 15"
        />

        <path
          className="prism-core"
          d="M290 218 405 354 225 392 290 218Z"
          fill="currentColor"
          filter="url(#core-glow)"
        />

        <g className="prism-piece-entry prism-piece-entry--left">
          <g className="prism-piece prism-piece--left" filter="url(#prism-shadow)">
            <path
              d="M280 64 106 480 235 390 290 225 280 64Z"
              fill="url(#prism-left)"
              stroke="#FFF9F4"
              strokeWidth="10"
              strokeLinejoin="round"
            />
            <path d="m106 480 129-90 10-31-119 82-20 39Z" fill="#DCA8AF" opacity="0.58" />
            <path d="M279 86 127 441" stroke="white" strokeOpacity="0.72" strokeWidth="3" />
          </g>
        </g>

        <g className="prism-piece-entry prism-piece-entry--bottom">
          <g className="prism-piece prism-piece--bottom" filter="url(#prism-shadow)">
            <path
              d="M106 480 525 405 390 354 235 390 106 480Z"
              fill="url(#prism-bottom)"
              stroke="#FFF9F4"
              strokeWidth="10"
              strokeLinejoin="round"
            />
            <path d="m235 390 155-36 27 10-206 47 24-21Z" fill="white" opacity="0.48" />
            <path d="m137 466 350-63" stroke="white" strokeOpacity="0.7" strokeWidth="3" />
          </g>
        </g>

        <g className="prism-piece-entry prism-piece-entry--right">
          <g className="prism-piece prism-piece--right" filter="url(#prism-shadow)">
            <path
              d="M525 405 280 64 290 225 390 354 525 405Z"
              fill="url(#prism-right)"
              stroke="#FFF9F4"
              strokeWidth="10"
              strokeLinejoin="round"
            />
            <path d="m280 64 10 161 17 22-10-157-17-26Z" fill="#DCA8AF" opacity="0.5" />
            <path d="m297 90 198 286" stroke="white" strokeOpacity="0.68" strokeWidth="3" />
          </g>
        </g>
      </svg>
    </div>
  )
}

function Hero() {
  const { locale } = usePreferences()
  const c = copy[locale]

  return (
    <section className="hero" id="top">
      <div className="hero__texture" aria-hidden="true" />
      <div className="hero-signal" aria-hidden="true">
        {Array.from({ length: 7 }, (_, index) => (
          <span className="hero-signal__line" key={index} />
        ))}
      </div>
      <div className="hero__content">
        <h1 data-hero-item>
          <span>{c.hero.title}</span>
          <em>{c.hero.titleAccent}</em>
        </h1>
        <p className="hero__description" data-hero-item>
          {c.hero.description}
        </p>
        <div className="hero__actions" data-hero-item>
          <a href="#work" className={buttonVariants({ variant: "outline" })}>
            {c.hero.explore}
            <ArrowDownRight aria-hidden="true" />
          </a>
          <a href="#open-source" className={buttonVariants({ variant: "ghost" })}>
            {c.hero.source}
          </a>
        </div>
      </div>
      <HeroPrism label={c.hero.visualLabel} />
    </section>
  )
}

function AboutIntro() {
  const { locale } = usePreferences()
  const c = copy[locale]

  return (
    <section className="about section-shell" aria-labelledby="about-title">
      <div className="section-index" aria-hidden="true">
        00
      </div>
      <div className="about__copy" data-reveal>
        <p className="eyebrow">{c.about.eyebrow}</p>
        <h2 id="about-title">{c.about.title}</h2>
      </div>
      <div className="about__body" data-reveal>
        <p>{c.about.description}</p>
        <div className="tag-row" aria-label={c.about.eyebrow}>
          {c.about.tags.map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
      </div>
    </section>
  )
}

function ProjectHeading({
  index,
  category,
  name,
  tagline,
}: {
  index: string
  category: string
  name: string
  tagline: string
}) {
  return (
    <div className="project-heading">
      <div className="project-heading__meta">
        <span>{index}</span>
        <span>{category}</span>
      </div>
      <h3>{name}</h3>
      <p>{tagline}</p>
    </div>
  )
}

function PropertiesVisual() {
  const { locale } = usePreferences()
  const c = copy[locale]

  return (
    <div className="properties-visual" aria-label={`${c.work.before} / ${c.work.after}`}>
      <figure className="property-frame property-frame--before">
        <img
          src={propertiesBefore}
          alt={c.work.before}
          loading="lazy"
          decoding="async"
        />
        <figcaption>{c.work.before}</figcaption>
      </figure>
      <ArrowUpRight className="property-transform" aria-hidden="true" />
      <figure className="property-frame property-frame--after">
        <img
          src={propertiesAfter}
          alt={c.work.after}
          loading="lazy"
          decoding="async"
        />
        <figcaption>{c.work.after}</figcaption>
      </figure>
    </div>
  )
}

function TrustRail({ type }: { type: "cardzone" | "bousla" }) {
  const { locale } = usePreferences()
  const c = copy[locale]

  if (type === "cardzone") {
    return (
      <div className="trust-rail trust-rail--single">
        <span className="trust-rail__label">{c.work.trusted}</span>
        <ExternalLink
          href="https://daleelstore.com"
          className="trusted-logo trusted-logo--daleel"
          label={`Daleel Store, ${c.accessibility.openExternal}`}
        >
          <img src={daleelStoreLogo} alt="Daleel Store" loading="lazy" />
          <strong>Daleel Store</strong>
        </ExternalLink>
      </div>
    )
  }

  return (
    <div className="trust-rail trust-rail--clients">
      <span className="trust-rail__label">{c.work.trusted}</span>
      <div className="trusted-grid">
        {bouslaClients.map((client) => (
          <ExternalLink
            key={client.name}
            href={client.href}
            className="trusted-logo"
            label={`${client.name}, ${c.accessibility.openExternal}`}
          >
            <img src={client.image} alt={client.name} loading="lazy" decoding="async" />
          </ExternalLink>
        ))}
      </div>
    </div>
  )
}

function CardzonePartners() {
  const { locale } = usePreferences()
  const c = copy[locale]

  return (
    <div className="partner-rail">
      <span className="trust-rail__label">{c.work.network}</span>
      <div className="partner-grid">
        {cardzonePartners.map((partner) => {
          const logo = (
            <>
              <img src={partner.image} alt={partner.name} loading="lazy" decoding="async" />
              <span>{partner.name}</span>
            </>
          )

          if ("href" in partner) {
            return (
              <ExternalLink
                key={partner.name}
                href={partner.href}
                className="partner-logo"
                label={`${partner.name}, ${c.accessibility.openExternal}`}
              >
                {logo}
              </ExternalLink>
            )
          }

          return (
            <div className="partner-logo" key={partner.name}>
              {logo}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ProjectLink({ href }: { href: string }) {
  const { locale } = usePreferences()
  const c = copy[locale]

  return (
    <ExternalLink
      href={href}
      className="project-link"
      label={`${c.work.visit}, ${c.accessibility.openExternal}`}
    >
      <span>{c.work.visit}</span>
      <ArrowUpRight aria-hidden="true" />
    </ExternalLink>
  )
}

function Projects() {
  const { locale } = usePreferences()
  const c = copy[locale]

  return (
    <section className="work-section section-shell" id="work" aria-labelledby="work-title">
      <header className="section-header" data-reveal>
        <p className="eyebrow">{c.work.eyebrow}</p>
        <h2 id="work-title">{c.work.title}</h2>
        <p>{c.work.description}</p>
      </header>

      <div className="project-stack">
        <article className="project project--properties" id="properties" data-reveal>
          <div className="project__visual">
            <PropertiesVisual />
          </div>
          <div className="project__copy">
            <ProjectHeading {...c.work.properties} />
            <p className="project__description">{c.work.properties.description}</p>
            <p className="project__detail">{c.work.properties.detail}</p>
            <ProjectLink href={projectLinks.properties} />
          </div>
        </article>

        <article className="project project--cardzone" id="cardzone" data-reveal>
          <div className="project__visual">
            <div className="project-logo project-logo--cardzone">
              <img src={cardzoneLogo} alt={c.work.cardzone.name} loading="lazy" />
            </div>
            <CardzoneRoutingPreview />
          </div>
          <div className="project__copy">
            <ProjectHeading {...c.work.cardzone} />
            <p className="project__description">{c.work.cardzone.description}</p>
            <p className="project__detail">{c.work.cardzone.detail}</p>
            <TrustRail type="cardzone" />
            <CardzonePartners />
            <ProjectLink href={projectLinks.cardzone} />
          </div>
        </article>

        <article className="project project--bousla" id="bousla" data-reveal>
          <div className="project__visual">
            <div className="project-logo project-logo--bousla">
              <img src={bouslaLogo} alt={c.work.bousla.name} loading="lazy" />
            </div>
            <BouslaIbnMajidPreview />
          </div>
          <div className="project__copy">
            <ProjectHeading {...c.work.bousla} />
            <p className="project__description">{c.work.bousla.description}</p>
            <p className="project__detail">{c.work.bousla.detail}</p>
            <TrustRail type="bousla" />
            <ProjectLink href={projectLinks.bousla} />
          </div>
        </article>
      </div>
    </section>
  )
}

const qrCells = [
  0, 1, 2, 3, 4, 6, 8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 29, 30, 31,
  32, 34, 36, 38, 40, 42, 43, 44, 45, 46, 49, 51, 54, 56, 57, 59, 60, 62, 64,
  65, 66, 68, 70, 72, 74, 76, 78, 80, 81, 82, 83, 84, 86, 88, 90, 92, 94,
  96, 98, 99, 100, 101, 102, 104, 106, 108, 110, 112, 114, 116, 118, 120,
]

function OpenSource() {
  const { locale } = usePreferences()
  const c = copy[locale]
  const [downloads, setDownloads] = useState(ZATCA_DOWNLOADS_FALLBACK)

  useEffect(() => {
    const controller = new AbortController()

    fetchZatcaDownloads(fetch, controller.signal)
      .then(setDownloads)
      .catch(() => undefined)

    return () => controller.abort()
  }, [])

  const formattedDownloads = new Intl.NumberFormat(
    locale === "ar" ? "ar-SA" : "en-US",
  ).format(downloads)

  return (
    <section className="open-source" id="open-source" aria-labelledby="source-title">
      <div className="open-source__inner section-shell">
        <header className="open-source__header" data-reveal>
          <p className="eyebrow eyebrow--inverse">{c.openSource.eyebrow}</p>
          <h2 id="source-title">{c.openSource.title}</h2>
          <p>{c.openSource.description}</p>
        </header>

        <article className="source-card" data-reveal>
          <div className="source-card__code" aria-hidden="true">
            <div className="code-topbar">
              <span />
              <span />
              <span />
              <b>invoice.rb</b>
            </div>
            <pre>
              <code>
                <span className="code-keyword">require</span> <span className="code-string">&quot;zatca&quot;</span>
                {"\n\n"}
                qr = <span className="code-constant">ZATCA</span>.render_qr_code(
                {"\n  "}tags: invoice_tags
                {"\n"})
              </code>
            </pre>
            <div className="qr-mark">
              {Array.from({ length: 121 }, (_, index) => (
                <i key={index} className={qrCells.includes(index) ? "is-filled" : undefined} />
              ))}
            </div>
          </div>
          <div className="source-card__copy">
            <div className="source-card__meta">
              <GitHubMark />
              <span>theamazingteam / zatca</span>
            </div>
            <h3>{c.openSource.project}</h3>
            <p>{c.openSource.projectDescription}</p>
            <div className="source-card__signals">
              <span className="phase-chip">{c.openSource.phase}</span>
              <div className="download-stat" title={c.openSource.packages}>
                <Download aria-hidden="true" />
                <strong>{formattedDownloads}</strong>
                <span>{c.openSource.downloads}</span>
                <small>{c.openSource.packages}</small>
              </div>
            </div>
            <ExternalLink
              href={projectLinks.zatca}
              className={buttonVariants({ variant: "outline" })}
              label={`${c.openSource.cta}, ${c.accessibility.openExternal}`}
            >
              <GitHubMark />
              {c.openSource.cta}
              <ArrowUpRight aria-hidden="true" />
            </ExternalLink>
          </div>
        </article>
      </div>
    </section>
  )
}

function Contact() {
  const { locale } = usePreferences()
  const c = copy[locale]

  return (
    <section className="contact section-shell" id="contact" aria-labelledby="contact-title">
      <div className="contact__panel" data-reveal>
        <p className="eyebrow eyebrow--inverse">{c.contact.eyebrow}</p>
        <h2 id="contact-title">{c.contact.title}</h2>
        <a className="contact__email" href="mailto:info@theamazingteam.com">
          <Mail aria-hidden="true" />
          <span>
            <small>{c.contact.emailLabel}</small>
            info@theamazingteam.com
          </span>
          <ArrowUpRight aria-hidden="true" />
        </a>
        <a
          className="contact__email contact__whatsapp"
          href="https://wa.me/966115165995"
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="currentColor">
            <path d="M20.52 3.48A11.91 11.91 0 0 0 12.04 0C5.46 0 .1 5.35.1 11.93c0 2.1.55 4.16 1.6 5.97L0 24l6.26-1.64a11.95 11.95 0 0 0 5.77 1.47h.01C18.62 23.83 24 18.48 24 11.9c0-3.19-1.24-6.18-3.48-8.42ZM12.04 21.8a9.9 9.9 0 0 1-5.05-1.38l-.36-.21-3.72.98.99-3.63-.24-.38a9.87 9.87 0 0 1-1.52-5.25c0-5.47 4.45-9.92 9.93-9.92a9.85 9.85 0 0 1 7.02 2.91 9.85 9.85 0 0 1 2.9 7.02c0 5.47-4.46 9.86-9.95 9.86Zm5.44-7.43c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.77-1.65-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.87 1.22 3.07c.15.2 2.1 3.2 5.09 4.49.71.31 1.27.49 1.7.62.71.23 1.36.2 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35Z" />
          </svg>
          <span>
            <small>{c.contact.whatsappLabel}</small>
            <bdi dir="ltr">+966115165995</bdi>
          </span>
          <ArrowUpRight aria-hidden="true" />
        </a>
      </div>
    </section>
  )
}

function Footer() {
  const { locale } = usePreferences()
  const c = copy[locale]
  const year = new Date().getFullYear()

  return (
    <footer className="footer">
      <div className="footer__top section-shell">
        <Wordmark />
        <p>{c.footer.statement}</p>
        <ArrowDownRight aria-hidden="true" />
      </div>
      <div className="footer__bottom section-shell">
        <span>© {year} {c.footer.rights}</span>
        <div>
          <a href="#work">{c.footer.work}</a>
          <ExternalLink href={projectLinks.github}>{c.footer.source}</ExternalLink>
          <a href="#contact">{c.footer.contact}</a>
          <a href="#top" aria-label={c.footer.top}>
            {c.footer.top}
            <ArrowUp aria-hidden="true" />
          </a>
        </div>
      </div>
    </footer>
  )
}

export default function App() {
  const root = useRef<HTMLDivElement>(null)
  const { locale } = usePreferences()
  const c = copy[locale]

  usePageMotion(root, locale)

  useEffect(() => {
    document.title = c.meta.title
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", c.meta.description)
  }, [c])

  return (
    <div ref={root} className="site" data-animated="true">
      <a className="skip-link" href="#main-content">
        {c.accessibility.skip}
      </a>
      <Header />
      <main id="main-content">
        <Hero />
        <AboutIntro />
        <Projects />
        <OpenSource />
        <Contact />
      </main>
      <Footer />
    </div>
  )
}
